import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Shared/Toast';

/**
 * ProviderAdminsPage — Block 3.
 *
 * Listet SECaaS-eigene Provider-Admin-Accounts (platform_role=platform_admin).
 * Backend: Platform-Admin-API /api/platform/admin/admins (GET/POST/DELETE +
 * {id}/reset-password). Frueher lief die Seite ueber den toten /api/provider/*-
 * Pfad (useProviderApi) -> 404; jetzt ueber useApi (Platform-API).
 */
export default function ProviderAdminsPage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const { showToast } = useToast();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('admin/admins');
      setAdmins(Array.isArray(data) ? data : (data?.items || []));
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (admin) => {
    if (!window.confirm(t('admin.admins.delete_confirm'))) return;
    try {
      await api(`admin/admins/${admin.id}`, { method: 'DELETE' });
      showToast(t('admin.admins.deleted'), 'success');
      load();
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    }
  };

  const handleResetPw = async (admin) => {
    const pw = window.prompt(t('admin.admins.reset_pw_prompt'));
    if (!pw) return;
    try {
      await api(`admin/admins/${admin.id}/reset-password`, {
        method: 'POST', body: { new_password: pw },
      });
      showToast(t('admin.admins.pw_reset_ok'), 'success');
    } catch (err) {
      showToast(t('common.error') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    }
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{t('admin.admins.title')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {t('admin.admins.subtitle')}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          {t('admin.admins.add')}
        </button>
      </div>

      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 10, overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('common.loading')}
          </div>
        ) : admins.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
            {t('admin.admins.empty')}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface)' }}>
                <th style={th}>{t('admin.admins.col.name')}</th>
                <th style={th}>{t('admin.admins.col.email')}</th>
                <th style={th}>{t('admin.admins.col.status')}</th>
                <th style={th}>{t('admin.admins.col.created')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('admin.admins.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {admins.map(a => (
                <tr key={a.id}>
                  <td style={td}>{a.display_name || '—'}</td>
                  <td style={td}>{a.email || a.username}</td>
                  <td style={td}>{a.is_active === false ? 'inactive' : 'active'}</td>
                  <td style={td}>{a.created_at?.slice?.(0, 10) || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleResetPw(a)}
                            style={{ marginRight: 8 }}>
                      {t('admin.admins.reset_pw')}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <CreateAdminModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); showToast(t('admin.admins.created'), 'success'); }}
          api={api}
          t={t}
        />
      )}
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '10px 14px',
  borderBottom: '1px solid var(--border)',
  fontWeight: 600, color: 'var(--text-muted)',
  textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em',
};
const td = {
  padding: '10px 14px', borderBottom: '1px solid var(--border)',
};

function CreateAdminModal({ onClose, onCreated, api, t }) {
  const [form, setForm] = useState({ email: '', display_name: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handle = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api('admin/admins', { method: 'POST', body: form });
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '90%', maxWidth: 440, padding: 28,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 18 }}>
          {t('admin.admins.create.title')}
        </div>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="field-label">{t('admin.admins.create.email')}</label>
            <input className="field-input" type="email" value={form.email}
                   onChange={handle('email')} required autoFocus />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.admins.create.name')}</label>
            <input className="field-input" type="text" value={form.display_name}
                   onChange={handle('display_name')} required />
          </div>
          <div className="form-group">
            <label className="field-label">{t('admin.admins.create.pw')}</label>
            <input className="field-input" type="password" value={form.password}
                   onChange={handle('password')} required minLength={8} />
          </div>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)',
              marginBottom: 12,
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('admin.admins.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
